import React, { forwardRef, useMemo } from 'react';
import MaterialButton from '@material-ui/core/Button';
import { fade, darken } from '@material-ui/core/styles/colorManipulator';
import Proptypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '../../lib/styles';
import getThemeColor, { validColors } from '../../lib/getThemeColor';

const useStyles = makeStyles(
  theme => {
    return {
      /* Styles applied to the root element if `variant="text"`. */
      textColor: {
        color: ({ color }) => getThemeColor(theme, color, 'main'),
        '&.focus-visible': {
          boxShadow: ({ color }) => `0px 0px 12px 2px ${getThemeColor(theme, color, 'main')}`,
        },
        '&:hover': {
          backgroundColor: ({ color }) =>
            fade(getThemeColor(theme, color, 'main'), theme.palette.action.hoverOpacity),
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: 'transparent',
          },
        },
        '&:active': {
          backgroundColor: ({ color }) => fade(getThemeColor(theme, color, 'light'), 0.3),
        },
        '&.Mui-disabled': {
          color: ({ color }) => getThemeColor(theme, color, 'main'),
        },
      },

      /* Styles applied to the root element if `variant="outlined"`. */
      outlinedColor: {
        color: ({ color }) => getThemeColor(theme, color, 'main'),
        border: ({ color }) => '1px solid '.concat(fade(getThemeColor(theme, color, 'main'), 0.5)),
        '&.focus-visible': {
          boxShadow: ({ color }) => `0px 0px 12px 2px ${getThemeColor(theme, color, 'main')}`,
        },
        '&:hover': {
          border: ({ color }) => '1px solid '.concat(getThemeColor(theme, color, 'main')),
          backgroundColor: ({ color }) => getThemeColor(theme, color, 'main'),
          color: ({ color }) => getThemeColor(theme, color, 'contrastText'),
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: 'transparent',
          },
        },
        '&:active': {
          backgroundColor: ({ color }) => getThemeColor(theme, color, 'dark'),
        },
        '&.Mui-disabled': {
          color: ({ color }) => getThemeColor(theme, color, 'main'),
          border: ({ color }) =>
            '1px solid '.concat(fade(getThemeColor(theme, color, 'main'), 0.5)),
        },
      },

      /* Styles applied to the root element if `variant="contained"`. */
      containedColor: {
        color: ({ color }) => getThemeColor(theme, color, 'contrastText'),
        backgroundColor: ({ color }) => getThemeColor(theme, color, 'main'),
        '&.focus-visible': {
          boxShadow: ({ color }) =>
            `0px 0px 12px 0px ${darken(getThemeColor(theme, color, 'main'), 0.5)}`,
        },
        '&:hover': {
          boxShadow: 'none',
          backgroundColor: ({ color }) => getThemeColor(theme, color, 'dark'),
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: ({ color }) => getThemeColor(theme, color, 'main'),
          },
        },
        '&:active': {
          backgroundColor: ({ color }) => darken(getThemeColor(theme, color, 'dark'), 0.1),
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          backgroundColor: ({ color }) => getThemeColor(theme, color, 'main'),
          color: ({ color }) => getThemeColor(theme, color, 'contrastText'),
        },
      },

      /* Styles applied to the root element if `variant="contained"`. */
      containedSoftColor: {
        color: ({ color }) => getThemeColor(theme, color, 'main'),
        backgroundColor: ({ color }) =>
          fade(getThemeColor(theme, color, 'main'), theme.palette.action.hoverOpacity),
        '&.focus-visible': {
          boxShadow: ({ color }) =>
            `0px 0px 12px 0px ${darken(getThemeColor(theme, color, 'main'), 0.5)}`,
        },
        '&:hover': {
          boxShadow: 'none',
          backgroundColor: ({ color }) => fade(getThemeColor(theme, color, 'light'), 0.3),
          // Reset on touch devices, it doesn't add specificity
          '@media (hover: none)': {
            backgroundColor: ({ color }) => getThemeColor(theme, color, 'main'),
          },
        },
        '&:active': {
          backgroundColor: ({ color }) => fade(getThemeColor(theme, color, 'dark'), 0.25),
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          backgroundColor: ({ color }) => getThemeColor(theme, color, 'main'),
          color: ({ color }) => getThemeColor(theme, color, 'contrastText'),
        },
      },
    };
  },
  { name: 'Button' }
);

const Button = forwardRef(function Button(props, ref) {
  const {
    children,
    color,
    disabled,
    fullWidth,
    onClick,
    size,
    type,
    variant,
    className,
    ...restProps
  } = props;
  const classes = useStyles({ color });
  const isValidColor = useMemo(() => validColors.includes(color), [color]);

  return (
    <MaterialButton
      {...restProps}
      variant={variant}
      className={clsx([
        className,
        variant === 'text' && isValidColor && classes.textColor,
        variant === 'outlined' && isValidColor && classes.outlinedColor,
        variant === 'contained' && isValidColor && classes.containedColor,
        variant === 'contained-soft' && isValidColor && classes.containedSoftColor,
      ])}
      disableRipple
      disabled={disabled}
      fullWidth={fullWidth}
      onClick={onClick}
      ref={ref}
      size={size}
      type={type}
      focusVisibleClassName="focus-visible"
    >
      {children}
    </MaterialButton>
  );
});

Button.propTypes = {
  children: Proptypes.node.isRequired,
  className: Proptypes.string,
  color: Proptypes.oneOf([
    'default',
    'primary',
    'secondary',
    'success',
    'info',
    'warning',
    'danger',
  ]),
  disabled: Proptypes.bool,
  fullWidth: Proptypes.bool,
  onClick: Proptypes.func,
  size: Proptypes.oneOf(['small', 'medium', 'large']),
  type: Proptypes.oneOf(['button', 'submit', 'reset']),
  variant: Proptypes.oneOf(['text', 'outlined', 'contained', 'contained-soft']),
};

Button.defaultProps = {
  className: '',
  color: 'default',
  disabled: false,
  fullWidth: false,
  onClick: null,
  size: 'medium',
  type: 'button',
  variant: 'text',
};

Button.displayName = 'Button';

export default Button;
